/**
 * 辞書API クライアント
 *
 * テナントは認証クッキーから自動解決されるため、リクエストに tenant_id を含めない。
 */

import type { DictionaryResponse, VersionCheckResponse } from "./types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

/**
 * 辞書を取得
 */
export async function fetchDictionary(): Promise<DictionaryResponse> {
    const response = await fetch(`${API_BASE_URL}/dictionary`, {
        credentials: "include",
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch dictionary: ${response.status}`);
    }

    return response.json();
}

/**
 * バージョンを確認
 */
export async function checkVersion(
    currentVersion: string,
    signal?: AbortSignal
): Promise<VersionCheckResponse> {
    const response = await fetch(
        `${API_BASE_URL}/dictionary/version?current_version=${encodeURIComponent(currentVersion)}`,
        { signal, credentials: "include" }
    );

    if (!response.ok) {
        throw new Error(`Failed to check version: ${response.status}`);
    }

    return response.json();
}

/**
 * テナント別の上書き設定を取得
 */
export async function fetchTenantOverrides(): Promise<Record<string, string>> {
    const response = await fetch(`${API_BASE_URL}/dictionary/overrides`, {
        credentials: "include",
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch overrides: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
}

/**
 * テナント別辞書の値を更新
 */
export async function updateTenantValue(
    key: string,
    value: string
): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/dictionary/company`, {
        method: "POST",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, value }),
    });

    if (!response.ok) {
        throw new Error(`Failed to update dictionary: ${response.status}`);
    }
}

/**
 * テナント別辞書の値を削除（デフォルトに戻す）
 */
export async function deleteTenantValue(key: string): Promise<void> {
    const response = await fetch(
        `${API_BASE_URL}/dictionary/company?key=${encodeURIComponent(key)}`,
        { method: "DELETE", credentials: "include" }
    );

    if (!response.ok) {
        throw new Error(`Failed to delete dictionary value: ${response.status}`);
    }
}
